import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { createCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { ResourceList } from '@/Core/UI/CrudResource/ResourceList'
import { CrudResourceConfig } from '@/Ship/Configs/CrudResourceConfig'

import { UserResource } from '../Resources/UserResource'

import { UserTopMenu, type UserListKind } from './UserTopMenu'

interface Props {
    kind: UserListKind
}

/**
 * Список співробітників одного стану.
 *
 * Дві сторінки — один ресурс під двома `prefilter` за статусом (ADR 038).
 * Нового співробітника створюють одразу активним, тож на «Деактивованих»
 * кнопки «Додати» немає: решту прав делегуємо інстансному `accessFn`, і лише
 * `create` там вимкнено (так само влаштовані сторінки видалених записів в еталоні ядра).
 */
export const UserRegistry = observer(({ kind }: Props) => {
    const registryKey = `Setting.User.${kind}.list`

    // `useState`, не `useMemo`: контекст списку мусить пережити рендер
    const [listContext] = useState(() =>
        createCrudResourceContext({
            contextType: 'list',
            registryKey,
            resource: UserResource,
            prefilter: { status: kind },
            ...(kind === 'inactive'
                ? {
                      accessFn: (params: { accessMode?: string }) =>
                          params?.accessMode === 'create' ? false : CrudResourceConfig.accessFn(params),
                  }
                : {}),
            // Заголовок розділу дає верхнє меню — другий над ним лише дублював би
            headerPage: { title: <></> },
            // Каст на межі нетипізованої .jsx-фабрики: її inferred-параметр не
            // містить resource/prefilter/accessFn — їх фабрика читає через
            // ...props у рантаймі
        } as unknown as Parameters<typeof createCrudResourceContext>[0])
    )

    return (
        <>
            <UserTopMenu current={kind} />
            <ResourceList crudContext={listContext} registryKey={registryKey} />
        </>
    )
})
